import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const companyKeys = {
  all: ['companies'],
  lists: () => [...companyKeys.all, 'list'],
  details: () => [...companyKeys.all, 'detail'],
  detail: (id) => [...companyKeys.details(), id],
};

/**
 * Hook para obtener todas las empresas
 */
export const useCompanies = () => {
  return useQuery({
    queryKey: companyKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('companies');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una empresa específica
 * @param {string} id - UUID de la empresa
 * @param {Object} options - Opciones adicionales para la query
 */
export const useCompany = (id, options = {}) => {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`companies/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva empresa
 */
export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyData) => {
      const response = await apiClient.post('companies', companyData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating company:', error);
    },
  });
};

/**
 * Hook para actualizar una empresa existente
 */
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyData) => {
      const response = await apiClient.put(`companies/${companyData.uuid}`, companyData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(companyKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating company:', error);
    },
  });
};

/**
 * Hook para eliminar una empresa
 */
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`companies/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      queryClient.removeQueries({ queryKey: companyKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting company:', error);
    },
  });
};
